import { TablePager } from '@fukuri-admins/ui/common';
import { CircularProgress } from '@mui/material';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { ContentsHeader } from '../../../components/common/ContentsHeader/index.tsx';
import { CreateFormClientTableContent } from '../../../components/pushNotification/CreateFormClientTableContent/index.tsx';
import { ClientType } from '../../../types/resources/ClientType.ts';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType.ts';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType.ts';
import { handleSorting } from '../../../utils/sortings.ts';
import styles from './styles.module.scss';

type Props = {
  fetchClientsData: ClientType[];
  setFetchClientsData: (fetchClientsData: ClientType[]) => void;
  sortParams: ActionTableSortParamsType;
  setSortParams: (sortParams: ActionTableSortParamsType) => void;
  isLoading: boolean;
  selectedClients: ClientType[];
  setSelectedClients: React.Dispatch<React.SetStateAction<ClientType[]>>;
  selectAllState: true | false | 'indeterminate';
  setSelectAllState: React.Dispatch<React.SetStateAction<true | false | 'indeterminate'>>;
  pagerProps: React.ComponentProps<typeof TablePager>;
};

export const ClientList: React.FC<Props> = ({
  fetchClientsData,
  setFetchClientsData,
  sortParams,
  setSortParams,
  isLoading,
  selectedClients,
  setSelectedClients,
  selectAllState,
  setSelectAllState,
  pagerProps,
}) => {
  // 配信対象企業の選択周りの修正はページング・企業名検索が揃うまで本番非公開にする
  const { enableStaging } = getEnvFeatureFlag();
  const selectedClientIds = selectedClients.map(client => client.id);

  // フラグ ON では全件選択チェックボックスの状態を「現在のページの選択状況」から毎回導出する。
  // state をそのまま表示に使うと、最後の 1 件のチェックを外しても indeterminate（横棒）が
  // 残り続ける・企業数が上限を超えるテナントで全選択しても ✓ にならない、という状態のズレが出る
  const getCurrentPageSelectAllState = (): true | false | 'indeterminate' => {
    const selectedCount = fetchClientsData.filter(client =>
      selectedClientIds.includes(client.id)
    ).length;

    // 0 件の判定を先に置く。順序を入れ替えると selectedCount === fetchClientsData.length が
    // 0 === 0 で成立し、一覧が空のときに ✓ を返してしまう
    if (selectedCount === 0) return false;
    if (selectedCount === fetchClientsData.length) return true;
    return 'indeterminate';
  };

  const resolvedSelectAllState = enableStaging ? getCurrentPageSelectAllState() : selectAllState;

  const tableHeader: ActionTableColumnsType[] = [
    { label: '', key: 'checkbox', sortable: false, filterable: false },
    { label: '企業名', key: 'name', sortable: false, filterable: false },
    { label: '利用者数', key: 'user_count', sortable: false, filterable: false },
  ];

  const toggleSelectForRow = (row: ClientType) => {
    const isChecked = selectedClientIds.includes(row.id);
    if (isChecked) {
      setSelectedClients(prev => prev.filter(client => client.id !== row.id));
    } else {
      setSelectedClients(prev => [...prev, row]);
    }

    // フラグ ON では表示状態を resolvedSelectAllState から導出するため、ここでの更新は不要
    if (enableStaging) return;

    setSelectAllState('indeterminate');
  };

  const handleToggleSelectAll = () => {
    if (enableStaging) {
      // フラグ ON では「現在のページ」だけを対象にする。
      // 他ページで選択済みの企業は残したまま、現在ページ分だけを足す・引く
      const currentPageIds = fetchClientsData.map(client => client.id);
      if (resolvedSelectAllState === true) {
        setSelectedClients(prev => prev.filter(client => !currentPageIds.includes(client.id)));
      } else {
        setSelectedClients(prev => [
          ...prev.filter(client => !currentPageIds.includes(client.id)),
          ...fetchClientsData,
        ]);
      }
      return;
    }

    if (selectAllState === false || selectAllState === 'indeterminate') {
      setSelectAllState(true);
    } else {
      setSelectAllState(false);
    }
  };

  const renderCheckbox = (row: ClientType) => {
    const isChecked = selectedClientIds.includes(row.id);

    return (
      <input
        type='checkbox'
        checked={isChecked}
        onChange={e => {
          e.stopPropagation();
          toggleSelectForRow(row);
        }}
        onClick={e => e.stopPropagation()}
        aria-label='企業を選択'
      />
    );
  };

  return (
    <div className={styles.Root} data-testid='client-page'>
      <ContentsHeader title='配信対象企業' className={styles.noMargin} />
      <div className={styles.tableContainer}>
        <div>
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <div className={styles.selectionInfoContainer}>
                <div
                  className={`${styles.selectionInfo} ${
                    selectedClientIds.length === 0 ? styles.hidden : ''
                  }`}
                >
                  {selectedClientIds.length > 0 &&
                    `${selectedClientIds.length}件の企業が選択されています。`}
                </div>
              </div>
              <CreateFormClientTableContent
                rows={fetchClientsData}
                columns={tableHeader}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    fetchClientsData,
                    setSortParams,
                    setFetchClientsData
                  )
                }
                handleToggleSelectAll={handleToggleSelectAll}
                renderCheckbox={renderCheckbox}
                toggleSelectForRow={toggleSelectForRow}
                selectAllState={resolvedSelectAllState}
              />
              {/* フラグ ON のときだけページャーを出す。
                  0 件のときはページ移動する先が無いので出さない。
                  ローディング中はこの分岐ごとスピナーに差し替わり、ページャーが消えることで
                  取得中の多重ページ送りを防いでいる（index.tsx の取得 effect のコメント参照） */}
              {enableStaging && pagerProps.totalCount > 0 && (
                <TablePager {...pagerProps} className={styles.pager} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
